import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import api, { asArray } from '../services/api';
import SignalTabsNav from '../components/SignalTabsNav';

function UpArrow() {
  return (
    <svg className="w-3 h-3" viewBox="0 0 20 20" fill="currentColor">
      <path d="M10 3l6 8H4l6-8z" />
    </svg>
  );
}

function DownArrow() {
  return (
    <svg className="w-3 h-3" viewBox="0 0 20 20" fill="currentColor">
      <path d="M10 17l-6-8h12l-6 8z" />
    </svg>
  );
}

/** Dark, rounded card rows — matches the Signals page's card treatment so the two
 *  tabs feel like one product instead of a light table bolted onto a dark app. */
function MomentumRow({ r, isUp }) {
  const changeUp = !String(r.changePercentage).startsWith('-');
  return (
    <Link
      to={`/stock/${encodeURIComponent(r.ticker)}`}
      className="flex items-center justify-between gap-3 px-4 py-3 rounded-xl bg-white/[0.03] hover:bg-white/[0.06] border border-white/5 transition"
    >
      <div className="min-w-0 flex items-center gap-3">
        <div
          className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 text-xs font-bold ${
            isUp ? 'bg-emerald-400/15 text-emerald-300' : 'bg-rose-400/15 text-rose-300'
          }`}
        >
          {isUp ? <UpArrow /> : <DownArrow />}
        </div>
        <div className="min-w-0">
          <p className="font-bold text-white tracking-tight truncate">{r.ticker}</p>
          <p className="text-[11px] text-slate-400 truncate max-w-[220px]">{r.reason || '—'}</p>
        </div>
      </div>
      <div className="text-right shrink-0">
        <p className={`font-bold tabular-nums ${changeUp ? 'text-emerald-400' : 'text-rose-400'}`}>${r.price}</p>
        <p className={`inline-flex items-center gap-0.5 text-xs font-bold tabular-nums ${
          changeUp ? 'text-emerald-400' : 'text-rose-400'
        }`}>
          {changeUp ? <UpArrow /> : <DownArrow />}
          {r.changePercentage}
        </p>
      </div>
    </Link>
  );
}

/** One panel for both directions, switched with ▲ Up / ▼ Down (was two panels). */
function MomentumPanel({ up, down }) {
  const [dir, setDir] = useState('up');
  const rows = dir === 'up' ? up : down;
  return (
    <div className="rounded-2xl border border-slate-700/60 bg-gradient-to-br from-slate-900 via-slate-900 to-slate-800 shadow-md overflow-hidden">
      <div className="px-4 py-3.5 border-b border-white/10 flex items-center justify-between gap-3">
        <p className="min-w-0 text-sm font-bold text-white">AI Move - {dir === 'up' ? 'Up' : 'Down'}</p>
        <div className="flex gap-1 rounded-lg bg-white/5 p-0.5 shrink-0">
          {[['up', '▲ Up'], ['down', '▼ Down']].map(([k, label]) => (
            <button
              key={k}
              type="button"
              onClick={() => setDir(k)}
              className={`rounded-md px-2.5 py-1 text-xs font-semibold transition ${
                dir === k ? 'bg-white text-slate-900' : 'text-slate-400 hover:text-white'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      <div className="p-2.5 space-y-1.5">
        {(rows || []).length === 0 ? (
          <p className="text-center text-slate-500 text-xs py-8">No data</p>
        ) : (
          rows.map((r, i) => <MomentumRow key={`${r.ticker}-${i}`} r={r} isUp={dir === 'up'} />)
        )}
      </div>
    </div>
  );
}

export default function AiSignals() {
  const [up, setUp] = useState([]);
  const [down, setDown] = useState([]);
  const [loading, setLoading] = useState(true);
  const hasData = useRef(false);

  useEffect(() => {
    let cancelled = false;
    const cached = sessionStorage.getItem('sx_ai');
    if (cached) {
      try {
        const p = JSON.parse(cached);
        if (p.up?.length || p.down?.length) {
          setUp(p.up || []);
          setDown(p.down || []);
          setLoading(false);
          hasData.current = true;
        }
      } catch (_) {}
    }

    (async () => {
      try {
        const [u, d] = await Promise.all([
          api.get('/stock/surge?limit=10'),
          api.get('/stock/surge-down?limit=10'),
        ]);
        if (cancelled) return;
        const upRows = asArray(u.data).slice(0, 10);
        const downRows = asArray(d.data).slice(0, 10);
        if (upRows.length || downRows.length) {
          setUp(upRows);
          setDown(downRows);
          hasData.current = true;
          sessionStorage.setItem('sx_ai', JSON.stringify({ up: upRows, down: downRows }));
        }
      } catch (_) {
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading && !hasData.current) {
    return <p className="text-center text-slate-400 py-16">Loading AI signals…</p>;
  }

  return (
    <div className="pb-8">
      <SignalTabsNav />
      {/* No clock here — only the Signals page shows the time. */}
      <div className="mb-4">
        <h1 className="text-2xl font-bold tracking-tight text-slate-900">Today&apos;s AI Signals</h1>
        <p className="mt-0.5 text-xs text-slate-500">Top 10 gainers / decliners by AI scan</p>
      </div>
      <MomentumPanel up={up} down={down} />
    </div>
  );
}
